import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Image,
  FiberManualRecord,
  Edit,
} from '@mui/icons-material';
import { HotKey, MessageType, DuringSettings } from '../types';
import { DEFAULT_HOTQUU_TILE_COLOR } from '../constants/hotQuuTheme';

function formatRotationLabel(type: MessageType): string {
  switch (type) {
    case MessageType.TAKEOVER:
      return 'Takeover';
    case MessageType.OVERRIDE:
      return 'Override';
    case MessageType.ROTATE_WITH:
      return 'Rotate with Billboards';
    default:
      return String(type);
  }
}

/** option_ids order: 1→M (Music), 2→O (Other), 3→A (Ads); multiple joined with + */
function formatDisplayDuringLabel(s: DuringSettings): string {
  const parts: string[] = [];
  if (s.music) parts.push('M');
  if (s.other) parts.push('O');
  if (s.ads) parts.push('A');
  return parts.length > 0 ? parts.join('+') : '—';
}

interface HotKeyCardProps {
  hotKey: HotKey;
  /** Card body (excluding edit control): publish / preview flow (#20 / #12). */
  onCardClick: (hotKey: HotKey) => void;
  /** Edit affordance: separate from card body (#20). */
  onEditClick: (hotKey: HotKey) => void;
}

const HotKeyCard: React.FC<HotKeyCardProps> = ({ hotKey, onCardClick, onEditClick }) => {
  const [imageError, setImageError] = React.useState(false);

  const cardBg = hotKey.tileColor || hotKey.category?.color || DEFAULT_HOTQUU_TILE_COLOR;

  const lineSx = {
    fontSize: '14px',
    lineHeight: 1.35,
    fontWeight: 400,
    color: '#ffffff',
  } as const;

  const handlePublishAreaClick = () => {
    onCardClick(hotKey);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onEditClick(hotKey);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0,
        border: 'none',
        outline: 'none',
        boxShadow: 'none',
        backgroundColor: cardBg,
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        transform: 'none',
        transition: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(255,255,255,0)',
          transition: 'background-color 0.2s ease-in-out',
          pointerEvents: 'none',
          zIndex: 0,
        },
        '&:hover::after': {
          backgroundColor: 'rgba(255,255,255,0.1)',
        },
      }}
    >
      <CardContent sx={{ p: 2, position: 'relative', zIndex: 1, '&:last-child': { pb: 2 } }}>
        {hotKey.isActive && (
          <Box
            component="span"
            title="On air"
            role="img"
            aria-label="This message is active on air"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
              lineHeight: 0,
              pointerEvents: 'none',
            }}
          >
            <FiberManualRecord
              sx={{
                fontSize: 18,
                color: '#4caf50',
                display: 'block',
                filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.45))',
              }}
            />
          </Box>
        )}

        <IconButton
          type="button"
          aria-label={`Edit ${hotKey.name || 'HotQuu'}`}
          onClick={handleEditClick}
          size="medium"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 2,
            width: 40,
            height: 40,
            p: 0,
            borderRadius: 0,
            color: '#ffffff',
            '&:hover': {
              color: '#ffffff',
              backgroundColor: 'rgba(255,255,255,0.14)',
            },
            '&:focus-visible': {
              outline: '2px solid rgba(255,255,255,0.9)',
              outlineOffset: 2,
            },
          }}
        >
          <Edit sx={{ fontSize: 24 }} />
        </IconButton>

        <Box
          onClick={handlePublishAreaClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handlePublishAreaClick();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Open publish preview for ${hotKey.name || 'HotQuu'}`}
          sx={{
            cursor: 'pointer',
            textAlign: 'left',
            pr: 5,
            outline: 'none',
            '&:focus-visible': {
              boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
            },
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flexShrink: 0 }}>
              {hotKey.messageImage && !imageError ? (
                <Avatar
                  src={hotKey.messageImage}
                  variant="square"
                  onError={() => setImageError(true)}
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 0,
                    objectFit: 'cover',
                    border: 'none',
                  }}
                />
              ) : (
                <Avatar
                  variant="square"
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 0,
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Image
                    sx={{
                      fontSize: 32,
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  />
                </Avatar>
              )}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 0 }}>
              <Typography component="div" sx={{ ...lineSx, mb: 0.5 }}>
                {hotKey.rt1 || hotKey.messageText.split(' - ')[0] || hotKey.messageText}
              </Typography>

              {(hotKey.rt2 || hotKey.messageText.includes(' - ')) && (
                <Typography component="div" sx={{ ...lineSx, mb: 0 }}>
                  {hotKey.rt2 || hotKey.messageText.split(' - ')[1]}
                </Typography>
              )}

              <Typography
                component="div"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: 1.45,
                  mt: 0.5,
                  pt: 0,
                }}
              >
                {formatRotationLabel(hotKey.type)} | {formatDisplayDuringLabel(hotKey.duringSettings)} |{' '}
                {hotKey.keepAliveMinutes}m/{hotKey.rotationDurationSeconds}s
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default HotKeyCard;
